<template>
  <app-page class="sdev-page" fill lean>
    <!-- Aufbau wie Geräte der Stammdaten (Kundenwunsch 13.10.2026: „superadminseiten bitte ebenfalls umstellen“):
         leichter Titel, rechts „Alle aufklappen“, „Aktualisieren“ und „Excel-Export“ mit Beschriftung (Handy:
         Icon-Knöpfe); links die Suche, rechts der Filter (Panel statt Filterzeile, „Aktiv“ wie bisher vorbelegt).
         Gruppiert nach Kunde wie bisher; ein Klick auf ein Gerät öffnet rechts den Detailbereich zum Bearbeiten -->
    <template #actions>
      <template v-if="!handy">
        <app-button :icon="expanded ? 'fold' : 'unfold'" @click="collapseAllClick">
          {{ expanded ? "Alle zuklappen" : "Alle aufklappen" }}
        </app-button>
        <app-button icon="refresh" @click="refreshDataGrid">Aktualisieren</app-button>
        <app-button icon="download" @click="exportToExcel">Excel-Export</app-button>
      </template>
      <template v-else>
        <app-icon-button
          :icon="expanded ? 'fold' : 'unfold'"
          :label="expanded ? 'Alle Gruppen zuklappen' : 'Alle Gruppen aufklappen'"
          @click="collapseAllClick"
        />
        <app-icon-button icon="refresh" label="Aktualisieren" @click="refreshDataGrid" />
        <app-icon-button icon="download" label="Excel-Export" @click="exportToExcel" />
      </template>
    </template>
    <template #toolbar>
      <app-search v-model="searchText" placeholder="In Geräten suchen" @input="onSearchInput" />
    </template>
    <template #toolbar-end>
      <sale-filter-menu
        :value="filters"
        :facets="facets"
        :result-count="resultCount"
        :description="filterTooltip"
        :segment-defs="filterSegments"
        :active-filters="activeFilterCount"
        :result-nouns="resultNouns"
        :empty-value="emptyDeviceFilters"
        :show-range="false"
        :align-right="!handy"
        @open="refreshFacets"
        @input="onFiltersInput"
      />
    </template>

    <div class="sdev-gridcol">
      <div class="app-grid-fill sdev-grid" :class="{ 'sdev-grid--auto': handy }">
        <dx-data-grid
          id="gridContainer"
          :ref="gridRefName"
          :height="gridHeight"
          :data-source="devicesDataSource"
          :columns="columns"
          :show-borders="false"
          :show-column-lines="false"
          :show-row-lines="true"
          :column-auto-width="false"
          :allow-column-reordering="!gridTouch"
          :allow-column-resizing="!gridTouch"
          :hover-state-enabled="!gridTouch"
          :scrolling="scrolling"
          :element-attr="gridAttr"
          :focused-row-enabled="true"
          :on-data-error-occurred="onDataErrorOccurred"
          @toolbar-preparing="onToolbarPreparing"
          @focused-row-changing="onFocusedRowChanging"
          @focused-row-changed="onFocusedRowChanged"
          @row-prepared="onRowPrepared"
          @content-ready="onContentReady"
        >
          <dx-paging :page-size="defaultPageSize" />
          <dx-pager :visible="false" />
          <dx-search-panel :visible="false" />
          <dx-selection mode="none" />
          <dx-grouping :auto-expand-all="false" expand-mode="rowClick" />
          <dx-summary>
            <dx-group-item :align-by-column="false" column="printer_id" summary-type="count" display-format="{0} Geräte" />
          </dx-summary>
          <template #druckerZelle="{ data }">
            <span class="sdev-printer">{{ data.value }}</span>
          </template>
          <!-- Status deutlich wie in den Stammdaten: Punkt mit „Aktiv“/„Inaktiv“, dahinter der Tag „Test“ -->
          <template #statusZelle="{ data }">
            <span class="sdev-status">
              <span
                class="sdev-status__dot"
                :class="isOne(data.data.active) ? 'sdev-status__dot--on' : 'sdev-status__dot--off'"
                aria-hidden="true"
              ></span>
              <span class="sdev-status__text">{{ isOne(data.data.active) ? "Aktiv" : "Inaktiv" }}</span>
              <app-tag v-if="isOne(data.data.is_test_device)" kind="test" />
            </span>
          </template>
          <template #sumupZelle="{ data }">
            <span v-if="isOne(data.value)" class="sdev-yes"><app-icon name="check" :size="14" class="sdev-yes__icon" />Ja</span>
            <span v-else class="sdev-muted"><span aria-hidden="true">–</span><span class="app-sr-only">Nein</span></span>
          </template>
          <template #zeitZelle="{ data }">
            <span class="sdev-num">{{ dateText(data.value) }}</span>
          </template>
          <template #mutedZelle="{ data }">
            <span class="sdev-muted">{{ data.value || "–" }}</span>
          </template>
        </dx-data-grid>
      </div>
      <app-grid-footer ref="fuss" :page-sizes="pageSizes">
        <template #summary>
          <span v-if="narrowed"><strong>{{ summaryCount }}</strong> von {{ summaryLoaded }}</span>
          <span v-else><strong>{{ summaryCount }}</strong> {{ summaryNoun }}</span>
        </template>
      </app-grid-footer>

      <app-dialog :value="discardDialog" title="Änderungen verwerfen?" @input="onDiscardDialogInput">
        <p class="sdev-dialog__text">Die ungespeicherten Änderungen gehen verloren.</p>
        <template #actions>
          <app-button @click="keepEditing">Weiter bearbeiten</app-button>
          <app-button kind="danger" @click="confirmDiscard">Verwerfen</app-button>
        </template>
      </app-dialog>
    </div>

    <template #detail>
      <app-detail-pane
        :open="!!editRow"
        icon="tablet"
        title="Gerät bearbeiten"
        close-label="Bearbeiten schließen"
        :dirty="dirty"
        @close="onDetailClose"
        @layout-change="onLayoutChange"
      >
        <template #subtitle>
          <app-tag v-if="editRow && !isOne(editRow.active)" kind="off" />
          <app-tag v-if="editRow && isOne(editRow.is_test_device)" kind="test" />
        </template>
        <device-editor
          v-if="editRow"
          variant="admin"
          mode="edit"
          :value="draft"
          :device="editRow"
          :errors="{}"
          @field="onField"
        />
        <template #footer>
          <span class="app-sr-only" role="status">{{ saveError || changeText }}</span>
          <span
            v-if="saveError || dirty"
            class="sdev-foot__text"
            :class="{ 'sdev-foot__text--error': !!saveError }"
            aria-hidden="true"
          >
            <app-icon v-if="saveError" name="alert" :size="14" />{{ saveError || changeText }}
          </span>
          <app-button :disabled="!dirty || saving" @click="revertChanges">Verwerfen</app-button>
          <app-button kind="primary" icon="check" :disabled="!dirty" :loading="saving" @click="saveChanges">
            Speichern
          </app-button>
        </template>
      </app-detail-pane>
    </template>
  </app-page>
</template>

<script>
import {
  DxDataGrid,
  DxPaging,
  DxSearchPanel,
  DxPager,
  DxSelection,
  DxGrouping,
  DxSummary,
  DxGroupItem,
} from "devextreme-vue/data-grid";
import CustomStore from "devextreme/data/custom_store";
import query from "devextreme/data/query";
import axios from "axios";
import DataGridMixin from "@/mixins/DataGridMixin";
import SaleFilterMenu from "@/components/sales/SaleFilterMenu";
import DeviceEditor from "@/components/masterdata/DeviceEditor.vue";
import { emptyFilters, activeCount, buildPredicate, buildFacets, describeFilters, NO_VALUE_TEXT } from "@/components/sales/saleFilters";
import { ADMIN_DEVICE_FILTER_CONFIG } from "@/components/masterdata/masterFilters";
import { exportWithColumns } from "@/utils/gridExport";
import { formatNumber, formatDate, plural } from "@/utils/format";

// Felder, die der Detailbereich speichert (wie bisher die bearbeitbaren Spalten der Tabelle)
const EDIT_FIELDS = ["device_id", "comment", "active", "enable_sumup", "is_test_device"];
const BOOLEAN_FIELDS = ["active", "enable_sumup", "is_test_device"];

function isOne(value) {
  return value === 1 || value === "1" || value === true;
}

function textValue(value) {
  return value === null || value === undefined ? "" : String(value);
}

function sameValue(a, b) {
  if (typeof a === "boolean" || typeof b === "boolean") {
    return !!a === !!b;
  }
  return textValue(a) === textValue(b);
}

function customerText(row) {
  return [row.customer_id, row.cust_printname].filter((v) => v !== null && v !== undefined && v !== "").join(" – ");
}

export default {
  components: {
    DxDataGrid,
    DxPaging,
    DxSearchPanel,
    DxPager,
    DxSelection,
    DxGrouping,
    DxSummary,
    DxGroupItem,
    SaleFilterMenu,
    DeviceEditor,
  },
  mixins: [DataGridMixin],

  computed: {
    handy() {
      return !!(this.$dichte && this.$dichte.handy);
    },
    gridHeight() {
      return this.handy ? undefined : "100%";
    },
    narrowed() {
      return this.summary.count !== this.summary.loaded;
    },
    summaryCount() {
      return formatNumber(this.summary.count);
    },
    summaryLoaded() {
      return formatNumber(this.summary.loaded) + " " + plural(this.summary.loaded, "Gerät", "Geräten");
    },
    summaryNoun() {
      return plural(this.summary.count, "Gerät", "Geräte") + " bei " + formatNumber(this.summary.customers) + " " +
        plural(this.summary.customers, "Kunde", "Kunden");
    },
    activeFilterCount() {
      return activeCount(this.filters, ADMIN_DEVICE_FILTER_CONFIG);
    },
    filterTooltip() {
      const parts = describeFilters(this.filters, this.filterLabel, 3, ADMIN_DEVICE_FILTER_CONFIG);
      return parts.length ? parts.join(" · ") : null;
    },
    dataUrl() {
      return `${this.$store.state.baseUrl}device`;
    },
    changedFields() {
      if (!this.editRow) {
        return [];
      }
      return EDIT_FIELDS.filter((key) => !sameValue(this.draft[key], this.original[key]));
    },
    dirty() {
      return this.changedFields.length > 0;
    },
    changeText() {
      const n = this.changedFields.length;
      if (!n) {
        return "Keine Änderungen";
      }
      return n === 1 ? "1 Änderung" : n + " Änderungen";
    },
  },
  data() {
    return {
      gridRefName: "dataGrid",
      gridAttr: { class: "app-grid--detail" },
      searchText: "",
      expanded: false,
      filters: emptyFilters(ADMIN_DEVICE_FILTER_CONFIG),
      filterSegments: ADMIN_DEVICE_FILTER_CONFIG.segments,
      resultNouns: ["Gerät", "Geräte"],
      facets: [],
      resultCount: null,
      summary: { count: 0, loaded: 0, customers: 0 },
      editRow: null,
      draft: {},
      original: {},
      saving: false,
      saveError: "",
      discardDialog: false,
      // Definitionsreihenfolge = Excel wie bisher (Kunde als Gruppe, dann die sichtbaren Spalten; ohne „Status“ und
      // die verborgene Kundennummer). Bildschirm: Drucker ID, Geräte ID, Status, SumUp, Bemerkung, Erstellt
      columns: [
        { dataField: "customer_id", caption: "Kunde", dataType: "number", visible: false },
        {
          dataField: "cust_printname",
          caption: "Kunde",
          visible: false,
          sortOrder: "asc",
          sortIndex: 0,
          groupIndex: 0,
          calculateDisplayValue: customerText,
        },
        { dataField: "printer_id", caption: "Drucker ID", alignment: "left", sortOrder: "asc", sortIndex: 1, width: 124, cellTemplate: "druckerZelle" },
        { dataField: "device_id", caption: "Geräte ID", alignment: "left", minWidth: 150 },
        { dataField: "comment", caption: "Bemerkung", alignment: "left", minWidth: 120, visibleIndex: 6, cellTemplate: "mutedZelle" },
        { dataField: "active", caption: "Aktiv", dataType: "boolean", visible: false },
        { dataField: "enable_sumup", caption: "SumUp", alignment: "left", width: 96, visibleIndex: 5, cellTemplate: "sumupZelle" },
        { dataField: "is_test_device", caption: "Testgerät", dataType: "boolean", visible: false },
        {
          dataField: "creation_time",
          caption: "Erstellt",
          alignment: "left",
          dataType: "date",
          format: "dd.MM.yyyy",
          width: 112,
          visibleIndex: 7,
          cellTemplate: "zeitZelle",
        },
        {
          name: "status",
          caption: "Status",
          width: 152,
          visibleIndex: 4,
          cellTemplate: "statusZelle",
          calculateCellValue: (row) => (isOne(row.active) ? "Aktiv" : "Inaktiv") + (isOne(row.is_test_device) ? " Test" : ""),
        },
      ],
      devicesDataSource: new CustomStore({
        key: ["customer_id", "printer_id", "device_id"],
        load: () =>
          axios
            .get(`${this.dataUrl}?customer_id=${this.$store.state.customerId}&option=allCustomers`)
            .then((response) => {
              this._rows = Array.isArray(response.data) ? response.data : [];
              this.refreshFacets();
              return response;
            }),
        update: (deviceKeys, deviceUpdates) =>
          axios.put(
            `${this.dataUrl}?customer_id=${deviceKeys.customer_id}&device_id=${deviceKeys.device_id}`,
            deviceUpdates
          ),
      }),
    };
  },
  created() {
    this._rows = null;
    this.pendingDiscard = null;
    this.pendingKeep = null;
  },
  mounted() {
    // Vorbelegung „Aktiv“ wie bisher der Filter der Spalte
    this.onFiltersInput(this.filters);
  },
  beforeRouteLeave(to, from, next) {
    if (!this.dirty || this.saving || !this.$store.state.isLoggedIn) {
      next();
      return;
    }
    this.askDiscard(
      () => next(),
      () => next(false)
    );
  },
  methods: {
    isOne,
    grid() {
      const ref = this.$refs[this.gridRefName];
      return ref && ref.instance ? ref.instance : null;
    },
    // ---------------------------------------------------------------- Filter-Panel
    emptyDeviceFilters() {
      return emptyFilters(ADMIN_DEVICE_FILTER_CONFIG);
    },
    filterLabel(listKey, key) {
      return key === "" ? NO_VALUE_TEXT : key;
    },
    refreshFacets() {
      this.facets = Object.freeze(buildFacets(this._rows || [], this.filters, this.filterLabel, ADMIN_DEVICE_FILTER_CONFIG));
    },
    onFiltersInput(filters) {
      this.filters = filters || this.emptyDeviceFilters();
      const grid = this.grid();
      if (grid) {
        const match = buildPredicate(this.filters, ADMIN_DEVICE_FILTER_CONFIG);
        grid.filter(match ? [match, "=", true] : null);
      }
      this.refreshFacets();
    },
    onContentReady(e) {
      const grid = e.component;
      const count = Number(grid.totalCount());
      this.resultCount = count >= 0 ? count : 0;
      // Kunden der sichtbaren Geräte (Filter und Suche wie das Grid)
      let customers = 0;
      try {
        const filter = grid.getCombinedFilter();
        const rows = this._rows || [];
        const visible = filter ? query(rows).filter(filter).toArray() : rows;
        customers = new Set(visible.map((row) => row.customer_id)).size;
      } catch (err) {
        customers = 0;
      }
      this.summary = { count: this.resultCount, loaded: (this._rows || []).length, customers };
      if (this.$refs.fuss) {
        this.$refs.fuss.update(grid);
      }
      this.syncEditRow();
    },
    onToolbarPreparing(e) {
      e.toolbarOptions.items = [];
      e.toolbarOptions.visible = false;
    },
    onRowPrepared(e) {
      if (e.rowType !== "data" || !e.data) {
        return;
      }
      const el = e.rowElement && e.rowElement.get ? e.rowElement.get(0) : e.rowElement;
      if (el && el.classList) {
        el.classList.toggle("sdev-row--off", !isOne(e.data.active));
      }
    },
    onSearchInput() {
      this.grid().searchByText(this.searchText || "");
    },
    dateText(value) {
      return value ? formatDate(value) : "–";
    },
    collapseAllClick() {
      const grid = this.grid();
      if (!grid) {
        return;
      }
      this.expanded = !this.expanded;
      if (this.expanded) {
        grid.expandAll();
      } else {
        grid.collapseAll();
      }
    },
    refreshDataGrid() {
      this.grid().refresh();
    },
    exportToExcel() {
      exportWithColumns(this.grid(), { exclude: ["status", "customer_id"] });
    },
    onDataErrorOccurred(e) {
      if (e && e.error) {
        e.error.message = "Geräte konnten nicht geladen werden.";
      }
    },

    // ---------------------------------------------------------------- Auswahl und Detailbereich
    onFocusedRowChanging(e) {
      if (this.saving) {
        e.cancel = true;
        return;
      }
      if (!this.dirty || e.newRowIndex === e.prevRowIndex) {
        return;
      }
      e.cancel = true;
      const index = e.newRowIndex;
      this.askDiscard(() => {
        const grid = this.grid();
        if (grid) {
          grid.option("focusedRowIndex", index);
        }
      });
    },
    onFocusedRowChanged(e) {
      const row = e.row;
      if (!row || row.rowType !== "data" || !row.data) {
        return;
      }
      if (this.isEditRow(row.data)) {
        return;
      }
      this.openEdit(row.data);
    },
    isEditRow(data) {
      return (
        !!this.editRow &&
        !!data &&
        String(data.customer_id) === String(this.editRow.customer_id) &&
        String(data.printer_id) === String(this.editRow.printer_id)
      );
    },
    findDevice(customerId, printerId) {
      const rows = Array.isArray(this._rows) ? this._rows : [];
      return rows.find((d) => d && String(d.customer_id) === String(customerId) && String(d.printer_id) === String(printerId)) || null;
    },
    openEdit(row) {
      const saved = Object.freeze(Object.assign({}, row));
      const draft = {
        printer_id: textValue(saved.printer_id),
        device_id: textValue(saved.device_id),
        comment: textValue(saved.comment),
        active: isOne(saved.active),
        enable_sumup: isOne(saved.enable_sumup),
        is_test_device: isOne(saved.is_test_device),
      };
      this.editRow = saved;
      this.draft = draft;
      this.original = Object.assign({}, draft);
      this.saveError = "";
    },
    syncEditRow() {
      if (!this.editRow || this.dirty || this.saving) {
        return;
      }
      const fresh = this.findDevice(this.editRow.customer_id, this.editRow.printer_id);
      if (fresh && EDIT_FIELDS.concat(["creation_time"]).some((key) => fresh[key] !== this.editRow[key])) {
        this.openEdit(fresh);
      }
    },
    closeDetail() {
      this.editRow = null;
      this.draft = {};
      this.original = {};
      this.saveError = "";
      const grid = this.grid();
      if (grid) {
        grid.option("focusedRowIndex", -1);
      }
    },
    onDetailClose() {
      if (this.saving) {
        return;
      }
      if (this.dirty) {
        this.askDiscard(() => this.closeDetail());
        return;
      }
      this.closeDetail();
    },
    // offener Detailbereich: Bemerkung und Erstellt ausblenden (stehen im Detail)
    onLayoutChange(e) {
      const grid = this.grid();
      if (grid) {
        const open = !!(e && e.open);
        grid.beginUpdate();
        grid.columnOption("comment", "visible", !open);
        grid.columnOption("creation_time", "visible", !open);
        grid.endUpdate();
      }
      this.$nextTick(() => {
        const current = this.grid();
        if (current) {
          current.updateDimensions();
        }
      });
    },
    onField(key, value) {
      this.draft = Object.assign({}, this.draft, { [key]: value });
      this.saveError = "";
    },
    revertChanges() {
      this.draft = Object.assign({}, this.original);
      this.saveError = "";
    },
    // nur geänderte Felder über dieselbe Schnittstelle wie bisher (PUT device, Schalter als 1/0)
    async saveChanges() {
      if (!this.dirty || this.saving) {
        return;
      }
      const draft = Object.assign({}, this.draft);
      const row = this.editRow;
      const changes = {};
      this.changedFields.forEach((key) => {
        changes[key] = BOOLEAN_FIELDS.indexOf(key) !== -1 ? (draft[key] ? 1 : 0) : draft[key];
      });
      this.saving = true;
      this.saveError = "";
      try {
        await this.devicesDataSource.update(
          { customer_id: row.customer_id, printer_id: row.printer_id, device_id: row.device_id },
          changes
        );
      } catch (err) {
        this.saving = false;
        this.saveError = "Änderungen konnten nicht gespeichert werden.";
        return;
      }
      this.original = draft;
      const grid = this.grid();
      if (grid) {
        try {
          await grid.refresh();
        } catch (err) {
          // Ladefehler zeigt das Grid selbst
        }
      }
      this.saving = false;
      if (this._isDestroyed || this.dirty) {
        return;
      }
      const fresh = this.findDevice(row.customer_id, row.printer_id);
      if (fresh) {
        this.openEdit(fresh);
        const current = this.grid();
        if (current) {
          const key = current.keyOf(fresh);
          if (current.getRowIndexByKey(key) >= 0) {
            current.option("focusedRowKey", key);
          }
        }
      }
    },

    // ---------------------------------------------------------------- Rückfrage „Änderungen verwerfen?“
    askDiscard(onDiscard, onKeep) {
      const previousKeep = this.pendingKeep;
      this.pendingDiscard = onDiscard;
      this.pendingKeep = onKeep || null;
      if (previousKeep) {
        previousKeep();
      }
      this.discardDialog = true;
    },
    confirmDiscard() {
      const action = this.pendingDiscard;
      this.pendingDiscard = null;
      this.pendingKeep = null;
      this.discardDialog = false;
      this.revertChanges();
      if (action) {
        action();
      }
    },
    keepEditing() {
      const action = this.pendingKeep;
      this.pendingDiscard = null;
      this.pendingKeep = null;
      this.discardDialog = false;
      if (action) {
        action();
      }
    },
    onDiscardDialogInput(open) {
      if (!open) {
        this.keepEditing();
      }
    },
  },
};
</script>

<style scoped>
.sdev-gridcol {
  flex: 999 1 560px;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.sdev-grid.sdev-grid--auto {
  flex: none;
}
/* Sortierung (Kunde, dann Drucker) ohne Ziffern im Kopf; die Pfeile bleiben */
.sdev-grid ::v-deep .dx-sort-index-icon {
  display: none;
}
.sdev-grid ::v-deep .sdev-printer {
  font-weight: 600;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}
.sdev-grid ::v-deep .sdev-num {
  font-variant-numeric: tabular-nums;
}
.sdev-grid ::v-deep .sdev-muted {
  color: var(--ink-3);
}
.sdev-grid ::v-deep .sdev-yes {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.sdev-grid ::v-deep .sdev-yes__icon {
  color: var(--green-dot);
}
.sdev-grid ::v-deep .sdev-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}
.sdev-grid ::v-deep .sdev-status__dot {
  flex: none;
  width: 8px;
  height: 8px;
  box-sizing: border-box;
  border-radius: 50%;
}
.sdev-grid ::v-deep .sdev-status__dot--on {
  background: var(--green-dot);
}
.sdev-grid ::v-deep .sdev-status__dot--off {
  border: 1.5px solid var(--ink-4);
}
.sdev-grid ::v-deep .sdev-status__text {
  font-weight: 500;
  color: var(--ink);
}
.sdev-grid ::v-deep .dx-data-row.sdev-row--off > td,
.sdev-grid ::v-deep .dx-data-row.sdev-row--off .sdev-printer,
.sdev-grid ::v-deep .dx-data-row.sdev-row--off .sdev-status__text {
  color: var(--ink-3);
}
.sdev-foot__text {
  flex: 1 1 100%;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  margin: -4px -16px 0;
  padding: 8px 16px;
  border-bottom: 1px solid var(--line-2);
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-2);
}
.sdev-foot__text--error {
  color: var(--red);
}
p.sdev-dialog__text {
  margin: 0;
}
</style>
